import { useCallback, useState, useMemo, useSyncExternalStore } from 'react';

import { useCurrentUserId } from './use-current-user-id';
import { LocalStorageService, PERSISTENT_KEY_PREFIX } from '../services/local-storage-service';

export const generatePersisentKey = (keyAccessor) => `${PERSISTENT_KEY_PREFIX}/${keyAccessor}`;
export const generateUserSpecificPersistentKey = (keyAccessor, userId) => `${PERSISTENT_KEY_PREFIX}/${userId}/${keyAccessor}`;

export const LS_HIDDEN_PRINTERS_KEY = 'hidden-printers';
export const LS_PRINT_SETTINGS_KEY = 'v3PrintSettings';
export const HIDDEN_ADJUSTMENTS_KEY = 'hiddenAdjustments';
export const SCAN_TO_SEARCH_MODAL_PERSISTENCE_PREFERENCE = 'SCAN_TO_SEARCH_MODAL_PERSISTENCE_PREFERENCE';
export const USER_ORDER_DETAILS_PINNED_NOTES = 'orderDetails/pinnedNotes';
export const USER_ORDER_DETAILS_EXPANDED_SECTION_STATE = 'orderDetails/expandedSectionState';
export const SETUP_SHIP_FROM_DRAFT = 'setupShipFromDraft';
export const USER_ORDER_DETAILS_SIDE_PANEL = 'orderDetails/sidePanel';
export const USER_LAST_PROMPTED_FOR_WALLET_UPDATE = 'lastUpdatePrompt';
export const RATE_VIEWER_SELECTED_VIEW = 'rateViewerSelectedView';
export const WORKFLOW_DATA = 'workflow_data';
export const TRIAL_INFO_BANNER_DISMISSED = 'trialInfoBannerDismissed';
export const TRIAL_WARNING_BANNER_DISMISSED = 'trialWarningBannerDismissed';
export const FIRST_TIME_UX_ADDRESS_CACHE = 'firstTimeUXAddressCache';
export const HAS_DISMISSED_ADDITIONAL_ITEMS_BANNER = 'hasDismissedAdditionalItemsBanner';
export const HAS_DISMISSED_EXPIRING_API_KEY_BANNER = 'hasDismissedExpiringApiKeyBanner';
export const HAS_DISMISSED_INSURANCE_COVERAGE_BANNER = 'hasDismissedInsuranceCoverageBanner';
export const HAS_DISMISSED_SHIPPING_LIMIT_BANNER = 'hasDismissedShippingLimitBanner';
export const HAS_DISMISSED_PLAN_TRIAL_BANNER = 'hasDismissedPlanTrialBanner';
export const HAS_DISMISSED_PLAN_TRIAL_EXPIRED_BANNER = 'hasDismissedPlanTrialExpiredBanner';
export const HAS_DISMISSED_PLAN_FEATURES_TRIAL_UPSELL_BANNER = 'hasDismissedPlanFeaturesTrialUpsellBanner';
export const WALLET_REGISTRATION_DATA = 'walletRegistrationData';
export const WALLET_UPDATE_DATA = 'walletUpdateData';
export const ADD_PAYMENT_METHOD_DATA = 'addPaymentMethod';
export const SUBSCRIPTION_CHANGE_PLAN_DATA = 'subscriptionChangePlan';
export const HAS_DISMISSED_RATE_CARD_UPGRADE_PROMO_BANNER = 'hasDismissedRateCardUpgradePromoBanner';
export const HAS_DISMISSED_BATCH_VIEW_UPGRADE_PROMO_BANNER = 'hasDismissedBatchViewUpgradePromoBanner';
export const PICKING_METHOD_PREFERENCE = 'pickingMethodPreference';

// Found this here: https://usehooks.com/useLocalStorage/
// Shamelessly ripped out the comments and changed it to use the local storage service that already has all the error handling and logging.
/**
 * @deprecated prefer the named export useLocalStorage
 * @param {string} key
 * @param {*} initialValue
 * @returns {*}
 */
export const useDeprecatedLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    const item = LocalStorageService.getItem(key);
    return item ?? initialValue;
  });

  const setValue = useCallback(
    (value) => {
      // Allow value to be a function, so we have same API as useState
      setStoredValue((prevState) => {
        const valueToStore = value instanceof Function ? value(prevState) : value;
        LocalStorageService.setItem(key, valueToStore);
        return valueToStore;
      });
    },
    [key],
  );

  return useMemo(() => {
    return [storedValue, setValue];
  }, [setValue, storedValue]);
};

/**
 * The version of the hook below is the one to prefer since it allow for n number of hook instances and all will sync with the underlying localStorage slice.
 */

const noop = () => null;

const subscribe = (callback = noop) => {
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener('storage', callback);
  };
};

const getValueFromLocalStorage = (key) => {
  const valueInLocalStorage = LocalStorageService.getItem(key);
  return valueInLocalStorage;
};

/**
 * Takes in a key param and returns the associated value from localStorage and a function to update it
 * @param {string} key localStorage accessor key
 * @param {*} [initialValue] fallback value when nothing is stored
 * @returns {[any, (any) => void]}
 */
export const useLocalStorage = (key, initialValue) => {
  const storedValue = useSyncExternalStore(subscribe, () => getValueFromLocalStorage(key));
  const value = storedValue ?? initialValue;

  /**
   * @param {any} newValue String or JSON.stringify-able value to be set in localStorage
   * @returns {void}
   */
  const setValue = useCallback(
    (newValue) => {
      LocalStorageService.setItem(key, newValue);
      window.dispatchEvent(new Event('storage'));
    },
    [key],
  );

  return [value, setValue];
};

/**
 * Hook for user-specific localStorage values (e.g., banner dismissals).
 * Automatically scopes the key to the current user ID.
 * @param {string} keyAccessor The base key (e.g., HAS_DISMISSED_PLAN_TRIAL_BANNER)
 * @returns {[any, (any) => void]}
 */
export const useUserSpecificLocalStorage = (keyAccessor) => {
  const userId = useCurrentUserId();
  const localStorageKey = generateUserSpecificPersistentKey(keyAccessor, userId);
  return useLocalStorage(localStorageKey);
};
